import { useCallback, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import {
  NumberHealthBannerView,
  useDeliverabilityBanner,
} from '#/entities/number-health'
import type { CustomerDeliverability } from '#/entities/number-health'
import { sanitizeSmsFragment, sanitizeSmsHtml } from '#/shared/lib/sms-sanitize'
import { useDictationSupported } from '#/shared/lib/voice-dictation'
import { cn } from '#/shared/lib/utils'
import { ArrowForwardIcon, AttachIcon, MicIcon } from '#/shared/ui/icons'
import { NoteEditor } from '#/shared/ui/note-editor'
import type {
  CompletionSource,
  NoteEditorVoiceControl,
} from '#/shared/ui/note-editor'
import {
  SmsCostPanel,
  SmsDeliverabilityNotice,
  SmsSanitizeNotice,
  SmsSegmentCounter,
} from '#/shared/ui/sms-segment-counter'
import { Tooltip } from '#/shared/ui/tooltip'
import {
  VoiceLoadingSpinner,
  VoiceProcessingIsland,
  VoiceRecordingIsland,
} from '#/shared/ui/voice-recording-island'

import type { ThreadMainline } from '../../model/conversation'
import type { ComposerState } from '../../model/use-composer'
import { useComposerDeliverability } from '../../model/use-composer-deliverability'
import { useActiveLine } from '../../model/use-phone-numbers'
import { ComposerAttachments } from './composer-attachments'
import {
  ComposerFromLineList,
  ComposerFromLineTrigger,
  ComposerMainlineLine,
} from './composer-from-line'

/** Hard cap on the SMS body — Twilio rejects a single message over 1600 chars.
 *  The rich editor can't hard-block typing the way `maxLength` did, so the send
 *  is disabled past the cap instead. */
const MAX_SMS_LENGTH = 1600

/** Mic state hoisted out of the editor: the pill's trailing button and the
 *  recording island drive the editor's in-place dictation via the control ref,
 *  and mirror its recording/stream state for the crossfade. */
function useComposerVoice() {
  const controlRef = useRef<NoteEditorVoiceControl | null>(null)
  const [state, setState] = useState<{
    recording: boolean
    stream: MediaStream | null
    loading: boolean
    processing: boolean
  }>({
    recording: false,
    stream: null,
    loading: false,
    processing: false,
  })
  const onStateChange = useCallback(
    (next: {
      recording: boolean
      stream: MediaStream | null
      loading: boolean
      processing: boolean
    }) => setState(next),
    [],
  )
  const start = useCallback(() => controlRef.current?.start(), [])
  const stop = useCallback(() => controlRef.current?.stop(), [])
  return { controlRef, onStateChange, start, stop, ...state }
}

type ComposerVoice = ReturnType<typeof useComposerVoice>

/** Left-side paperclip that opens the file picker — hidden on an iMessage
 *  line (text-only; the backend drops media). */
function AttachButton({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t('phonePanel.conversation.attach')}
      className="flex h-10 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 transition-colors duration-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-500"
    >
      <AttachIcon className="size-4" />
    </button>
  )
}

/** aria-label key for the send/mic button across its loading, idle-mic, and
 *  send states — pulled out to keep the button's own complexity in check. */
function actionAriaKey(loading: boolean, mic: boolean): string {
  if (loading) return 'voice.preparing'
  return mic ? 'phonePanel.conversation.voice' : 'phonePanel.conversation.send'
}

/** Hover content for the send/mic button: send hint when there's text, the
 *  preparing note while the mic opens, else the record hint. */
function ActionTooltipContent({
  active,
  loading,
  micAvailable,
}: {
  active: boolean
  loading: boolean
  micAvailable: boolean
}) {
  const { t } = useTranslation()
  if (active || !micAvailable) return <>{t('phonePanel.conversation.send')}</>
  return <>{loading ? t('voice.preparing') : t('voice.record')}</>
}

/** The mic↔send-arrow crossfade inside the trailing button. */
function ActionIcons({ showMic }: { showMic: boolean }) {
  return (
    <>
      <MicIcon
        className={cn(
          'absolute size-5 transition-opacity duration-200',
          showMic ? 'opacity-100' : 'opacity-0',
        )}
      />
      <ArrowForwardIcon
        className={cn(
          'absolute size-5 -rotate-90 transition-opacity duration-200',
          showMic ? 'opacity-0' : 'opacity-100',
        )}
      />
    </>
  )
}

/** Trailing button inside the pill: mic when empty, send arrow once you type.
 *  Tapping the mic starts voice recording (see VoiceRecordingIsland). */
function ComposerActionButton({
  active,
  loading,
  onSend,
  onMic,
  disabled,
  micAvailable,
}: {
  active: boolean
  loading: boolean
  onSend: () => void
  onMic: () => void
  disabled: boolean
  /** Whether this device can dictate at all — without it the idle button is a
   *  disabled send arrow rather than a mic that can't record. */
  micAvailable: boolean
}) {
  const { t } = useTranslation()
  const showMic = !active && micAvailable
  return (
    <Tooltip
      content={
        <ActionTooltipContent
          active={active}
          loading={loading}
          micAvailable={micAvailable}
        />
      }
    >
      <button
        type="button"
        onClick={active ? onSend : onMic}
        disabled={disabled || loading || (!active && !micAvailable)}
        aria-label={t(actionAriaKey(loading, showMic))}
        className={cn(
          'relative flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40',
          active
            ? 'w-12 bg-blue-500 text-paper hover:brightness-110'
            : 'w-9 text-gray-500 hover:text-gray-900',
        )}
      >
        {/* Opening the mic / transcription stream — spinner until it's live. */}
        {loading && !active ? (
          <VoiceLoadingSpinner className="size-5" />
        ) : (
          <ActionIcons showMic={showMic} />
        )}
      </button>
    </Tooltip>
  )
}

/** Trailing control area. Holds the send/mic button and the recording island
 *  stacked on top of each other and crossfades (opacity + scale) between them
 *  while the wrapper width morphs — so the mic↔island swap reads as one smooth
 *  shape change instead of a hard cut. */
function ComposerTrailing({
  recording,
  loading,
  processing,
  active,
  disabled,
  stream,
  onSend,
  onStartRecording,
  onStopRecording,
}: {
  recording: boolean
  loading: boolean
  processing: boolean
  active: boolean
  disabled: boolean
  stream: MediaStream | null
  onSend: () => void
  onStartRecording: () => void
  onStopRecording: () => void
}) {
  const micAvailable = useDictationSupported()
  // Both the listening island and the transcribing island take the wide slot.
  const wide = recording || processing
  return (
    <div
      data-coach="mic"
      className={cn(
        'relative flex h-9 shrink-0 items-center justify-end transition-[width] duration-300 ease-out',
        wide ? 'w-16' : active ? 'w-12' : 'w-9',
      )}
    >
      <div
        className={cn(
          'absolute inset-y-0 right-0 transition-all duration-200 ease-out',
          wide
            ? 'pointer-events-none scale-75 opacity-0'
            : 'scale-100 opacity-100',
        )}
      >
        <ComposerActionButton
          active={active}
          loading={loading}
          disabled={disabled}
          micAvailable={micAvailable}
          onSend={onSend}
          onMic={onStartRecording}
        />
      </div>
      <VoiceRecordingIsland
        onStop={onStopRecording}
        stream={stream}
        className={cn(
          'absolute inset-0 w-full transition-all duration-200 ease-out',
          recording
            ? 'scale-100 opacity-100'
            : 'pointer-events-none scale-90 opacity-0',
        )}
      />
      <VoiceProcessingIsland
        className={cn(
          'absolute inset-0 h-9 w-full transition-all duration-200 ease-out',
          processing
            ? 'scale-100 opacity-100'
            : 'pointer-events-none scale-90 opacity-0',
        )}
      />
    </div>
  )
}

/** The bordered input pill: the rich-text editing surface, the hidden file
 *  input, and the trailing control that swaps between send/mic and the voice
 *  island. Pasted files stage as attachments; plain text pastes through. */
function ComposerPill({
  composer,
  fileInputRef,
  voice,
  onEnterSubmit,
  autoFocus,
  active,
  disabled,
  allowMedia,
  onSend,
  completionSource,
}: {
  composer: ComposerState
  fileInputRef: RefObject<HTMLInputElement | null>
  voice: ComposerVoice
  onEnterSubmit?: () => void
  autoFocus: boolean
  active: boolean
  disabled: boolean
  /** False on an iMessage line — pasted files must not stage either. */
  allowMedia: boolean
  onSend: () => void
  completionSource?: CompletionSource
}) {
  const { t } = useTranslation()
  const { text, setText, addFiles } = composer
  return (
    <div
      className={cn(
        'flex min-h-10 flex-1 items-end rounded-3xl border border-gray-200 bg-paper-input py-0.5 pr-0.5 pl-3 backdrop-blur-sm',
        disabled && 'opacity-60',
      )}
    >
      <NoteEditor
        className="min-w-0 flex-1 self-center"
        value={text}
        onChange={setText}
        placeholder={t('phonePanel.conversation.placeholder')}
        hideToolbar
        minHeight={32}
        bodyClassName="max-h-30 resize-none px-0 py-1.5"
        autoFocus={autoFocus}
        disabled={disabled}
        onEnterSubmit={onEnterSubmit}
        onPasteFiles={allowMedia ? addFiles : undefined}
        transformPastedText={sanitizeSmsFragment}
        transformPastedHTML={sanitizeSmsHtml}
        voiceControlRef={voice.controlRef}
        onVoiceStateChange={voice.onStateChange}
        completionSource={completionSource}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files)
          event.target.value = ''
        }}
      />
      <ComposerTrailing
        recording={voice.recording}
        loading={voice.loading}
        processing={voice.processing}
        active={active}
        disabled={disabled}
        stream={voice.stream}
        onSend={onSend}
        onStartRecording={voice.start}
        onStopRecording={voice.stop}
      />
    </div>
  )
}

/** Quiet stand-in shaped like the input pill, shown while the recipient's
 *  number-health is still loading — see the gate in ConversationComposer. */
function ComposerPendingPlaceholder() {
  return (
    <div className="flex shrink-0 flex-col gap-1.5 px-4 pt-1 pb-8">
      <div className="flex items-end gap-1">
        <div className="h-10 w-9 shrink-0" aria-hidden />
        <div className="h-10 flex-1 animate-pulse rounded-3xl border border-gray-200 bg-paper-input" />
      </div>
    </div>
  )
}

/** Whether media may be staged. A premium line has no MMS — the backend
 *  silently drops media_urls, so the attach button and file-paste are
 *  withheld. A shared-line thread sends from the shared line itself: look it up
 *  among the member's lines to see if it's premium (not found — e.g. an
 *  unowned Telnyx mainline — falls back to allowed). */
function useAllowMedia(mainline?: ThreadMainline): boolean {
  const { lines, active } = useActiveLine()
  if (!mainline) return !active?.isImessage
  const sharedLine = lines.find((option) => option.id === mainline.id)
  return !sharedLine?.isImessage
}

/** The send handler pair. Sending ends a dictation session first — otherwise
 *  it would keep transcribing into the cleared draft. Enter sends; Shift+Enter
 *  inserts a newline; the chat-submit is only wired when there's a send
 *  handler (the compose-new view leaves Enter as a newline). */
function buildSubmit({
  voice,
  onSend,
  reset,
  active,
}: {
  voice: ComposerVoice
  onSend?: () => Promise<boolean>
  reset: () => void
  active: boolean
}) {
  const submit = async () => {
    voice.stop()
    if (!onSend) {
      reset()
      return
    }
    if (await onSend()) reset()
  }
  const onEnterSubmit = onSend
    ? () => {
        if (active) void submit()
      }
    : undefined
  return { submit, onEnterSubmit }
}

/** Everything that has to hold before the draft can be sent. */
function canSubmit({
  hasContent,
  overLimit,
  sending,
  disabled,
  blocked,
}: {
  hasContent: boolean
  overLimit: boolean
  sending: boolean
  disabled: boolean
  blocked: boolean
}): boolean {
  return hasContent && !overLimit && !sending && !disabled && !blocked
}

/** The "From" line row: the label plus either the shared-mainline line (static)
 *  or the personal-line picker (trigger + downward accordion list). */
function ComposerFromRow({
  text,
  mainline,
}: {
  text: string
  mainline?: ThreadMainline
}) {
  const { t } = useTranslation()
  const [fromOpen, setFromOpen] = useState(false)
  return (
    // From-row + its accordion share one gap-child so the collapsed list adds
    // no extra spacing above the attachments.
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex min-w-0 items-center gap-1">
          <span className="text-warm-500">
            {t('phonePanel.conversation.from')}
          </span>
          {mainline ? (
            <ComposerMainlineLine mainline={mainline} />
          ) : (
            <ComposerFromLineTrigger
              open={fromOpen}
              onToggle={() => setFromOpen((prev) => !prev)}
            />
          )}
        </div>
        <SmsSegmentCounter text={text} />
      </div>
      {!mainline && (
        <ComposerFromLineList
          open={fromOpen}
          onClose={() => setFromOpen(false)}
        />
      )}
    </div>
  )
}

interface ConversationComposerProps {
  composer: ComposerState
  /** Sends the draft (returns true to clear); omitted = clear only (compose-new). */
  onSend?: () => Promise<boolean>
  sending?: boolean
  /** Focus the editor on mount — used when a chat is opened. */
  autoFocus?: boolean
  /** Lock the whole composer — e.g. compose-new before a recipient is set. */
  disabled?: boolean
  /** Shared mainline the thread sends from — when set, the "From" row shows it
   *  statically instead of the personal-line picker. */
  mainline?: ThreadMainline
  /** The counterparty. Passing it lets the pre-send check also report the
   *  recipient-side blocks (opted out, spam cooldown, undeliverable). */
  toNumber?: string
  /** Counterparty name — lets the number-health banner address them by name. */
  contactName?: string
  /** Resolved counterparty customer with inline SMS deliverability; `null` =
   *  confirmed customer-less (fall back to the number-health lookup),
   *  `undefined` = still resolving. Compose-new leaves it unset. */
  customer?: CustomerDeliverability | null
  /** The counterparty opted this line pair out of SMS (conversation detail). */
  optedOut?: boolean
  /** Smart-compose source for the editor's inline autocomplete (Tab accepts).
   *  Omit to disable. */
  completionSource?: CompletionSource
}

/**
 * Composer gate. A recipient whose number can't receive SMS gets the reason
 * banner instead of an input; until their deliverability is known we hold a
 * placeholder rather than flash the input and then swap it for the banner.
 * A resolved customer settles instantly from its inline fields; only a
 * confirmed customer-less number consults the number-health lookup.
 * Everything else renders the live input via {@link ComposerInputArea}.
 */
export function ConversationComposer(props: ConversationComposerProps) {
  const { toNumber, contactName, customer, optedOut } = props
  const { banner, resolved: healthResolved } = useDeliverabilityBanner({
    customer,
    optedOut,
    phone: toNumber,
  })

  if (banner) {
    return (
      <div className="flex shrink-0 flex-col px-4 pt-3 pb-4">
        <NumberHealthBannerView banner={banner} contactName={contactName} />
      </div>
    )
  }
  if (toNumber && !healthResolved) return <ComposerPendingPlaceholder />

  return <ComposerInputArea {...props} />
}

/** The live input path: draft state, deliverability pre-check, and the pill. */
function ComposerInputArea({
  composer,
  onSend,
  sending = false,
  autoFocus = false,
  disabled = false,
  mainline,
  toNumber,
  completionSource,
}: ConversationComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    text,
    setText,
    plainText,
    attachments,
    removeAttachment,
    reset,
    hasContent,
  } = composer
  const voice = useComposerVoice()
  const deliverability = useComposerDeliverability({
    body: plainText,
    mainline,
    toNumber,
    enabled: !disabled,
  })
  const allowMedia = useAllowMedia(mainline)

  const overLimit = plainText.length > MAX_SMS_LENGTH
  // `blocked` = the send is refused before it ever reaches the carrier (opted
  // out, line can't text, quota spent). The notice below says which, so don't
  // let the user fire a message that cannot leave.
  const active = canSubmit({
    hasContent,
    overLimit,
    sending,
    disabled,
    blocked: deliverability.blocked,
  })

  const { submit, onEnterSubmit } = buildSubmit({
    voice,
    onSend,
    reset,
    active,
  })

  return (
    <div className="flex shrink-0 flex-col gap-1.5 px-4 pt-1 pb-8">
      <ComposerFromRow text={plainText} mainline={mainline} />
      <SmsDeliverabilityNotice
        findings={deliverability.findings}
        blockers={deliverability.blockers}
      />
      <SmsSanitizeNotice html={text} />
      <SmsCostPanel html={text} plainText={plainText} onChange={setText} />
      <ComposerAttachments
        attachments={attachments}
        onRemove={removeAttachment}
      />
      <div className="flex items-end gap-1">
        {allowMedia && (
          <AttachButton
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
          />
        )}
        <ComposerPill
          composer={composer}
          fileInputRef={fileInputRef}
          voice={voice}
          onEnterSubmit={onEnterSubmit}
          autoFocus={autoFocus}
          active={active}
          disabled={disabled}
          allowMedia={allowMedia}
          onSend={() => void submit()}
          completionSource={completionSource}
        />
      </div>
    </div>
  )
}
